/* eslint-disable local/ensure-wac-token-usage */

import { ReactNode } from 'react';

import { css } from '@compiled/react';

import { IconNameTypes } from '../../../../libraries/wpl/foundations/src/icon';
import {
	ContentBlock,
	CTAProps,
	HeaderTagTypes,
	OrientationTypes,
} from '../../content-block/src/content-block';
import { AspectRatios, PictureProps } from '../../image/src/image';

type ColorBarPositions = 'top' | 'bottom' | 'both' | 'none';

/**
 * @deprecated - Please use @/libraries/design/components/card instead
 */
export interface CardProps {
	headerText: string | ReactNode;
	bodyText?: string | ReactNode;
	headerType?: HeaderTagTypes;
	borderHeader?: boolean;
	eyebrowText?: string;
	footerHeader?: string;
	footerText?: string;
	/**
	 * @deprecated - Please use pictureProps instead
	 */
	imageLink?: string;
	/**
	 * @deprecated - Please use pictureProps instead
	 */
	imageAltText?: string;
	backgroundColor?: string;
	textColor?: string;
	iconName?: IconNameTypes;
	iconColor?: string;
	iconSize?: 'xs' | 'small' | 'medium' | 'large';
	orientation?: OrientationTypes;
	/**
	 * @deprecated - Please use pictureProps instead
	 */
	aspectRatio?: AspectRatios;
	colorBars?: ColorBarPositions;
	barColor?: string;
	ctaProps?: CTAProps;
	className?: string;
	pictureProps?: PictureProps;
	clickableCardUrl?: string;
}

const containerStyles = css({
	borderRadius: '2px',
	boxShadow: '0px 4px 6px 3px #42526e29',
	display: 'flex',
	flexDirection: 'column',
	maxWidth: '1100px',
	containerType: 'inline-size',
	height: '100%',
});

const contentBlockStyles = css({
	height: '100%',
});

const barStyles = css({
	margin: 0,
	padding: 0,
	width: '100%',
	height: '8px',
});

/**
 * @deprecated - Please use @/libraries/design/components/card instead
 */
export const Card = (props: CardProps) => {
	const {
		textColor,
		headerType,
		headerText,
		bodyText,
		eyebrowText,
		footerHeader,
		footerText,
		imageLink,
		imageAltText,
		backgroundColor,
		colorBars,
		iconName,
		iconColor,
		iconSize,
		barColor = '',
		className,
		ctaProps,
		aspectRatio,
		orientation,
		pictureProps,
		clickableCardUrl,
	} = props;

	if (imageLink && !imageAltText) {
		throw new Error('If using an image, please provide an alt text for accessibility purposes!');
	}

	const cardBackBackgroundColor = backgroundColor ?? '#ffffff';
	const cardBackTextColor = textColor ?? '#091e42';

	const cardBackColor = css({
		backgroundColor: cardBackBackgroundColor,
		color: cardBackTextColor,
	});

	const contentBlockIconColor = iconColor ?? '#091e42';

	const handleClick = () => {
		if (clickableCardUrl) {
			window.location.href = clickableCardUrl;
		}
	};

	const clickableCardStyle = css({
		cursor: `${clickableCardUrl ? 'pointer' : 'default'}`,
	});

	return (
		// Clickable cards should also have a link with a tab stop in the body of the card that points
		// to the same URL, so making this focusable or adding a keydown handler is probably unnecessary
		// eslint-disable-next-line @atlassian/a11y/click-events-have-key-events, @atlassian/a11y/no-static-element-interactions, @atlassian/a11y/interactive-element-not-keyboard-focusable
		<div
			className={className}
			css={[containerStyles, cardBackColor, clickableCardStyle]}
			onClick={handleClick}
		>
			{colorBars === 'top' || colorBars === 'both' ? (
				<div
					style={{
						backgroundColor: barColor,
						borderTopRightRadius: '4px',
						borderTopLeftRadius: '4px',
					}}
					css={barStyles}
				/>
			) : (
				''
			)}

			<ContentBlock
				css={contentBlockStyles}
				cardStyle={true}
				aspectRatio={aspectRatio}
				orientation={orientation}
				headerType={headerType}
				headerText={headerText}
				bodyText={bodyText}
				imageLink={imageLink}
				imageAltText={imageAltText}
				eyebrowText={eyebrowText}
				footerHeader={footerHeader}
				footerText={footerText}
				iconColor={contentBlockIconColor}
				iconName={iconName}
				iconSize={iconSize}
				ctaProps={ctaProps}
				pictureProps={pictureProps}
			/>

			{colorBars === 'bottom' || colorBars === 'both' ? (
				<div
					style={{
						backgroundColor: barColor,
						borderBottomRightRadius: '4px',
						borderBottomLeftRadius: '4px',
					}}
					css={barStyles}
				/>
			) : (
				''
			)}
		</div>
	);
};
